import React, { useEffect, useId, useState } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import './Dropdown.scss'
import { CheckIcon, Crevron } from '../../../assets/icons'
import { useRef } from 'react'
import RadioControl from '../RadioControl'
import FloatingPanel from '../FloatingPanel'
import MenuItem from '@/components/dropdown-menu/MenuItem'
import Button from '@/components/ui/button'
import { i18n } from '@/locale/i18n'

// The Dropdown component allows users to select a value from a dropdown list.
const Dropdown = ({
  // Design System Props
  type = 'default', // 'default' | 'outline' - Visual style type
  state = 'default', // 'default' | 'hover' | 'active' | 'disabled' | 'loading' - Component state
  size = 'medium', // 'small' | 'medium' | 'large' - Component size
  fontSize = '14px', // '12px' | '14px' - Text size
  iconStart = null, // Icon to display at the start
  hasLabel = false, // Whether component has a label
  label = null, // Name rendered above the dropdown
  isOpen: controlledIsOpen = null, // Controlled open state

  // Existing Props (keeping for backward compatibility)
  placeholder = '', // Placeholder text displayed when no value is selected.
  keepPlaceholder = false, // Determines if placeholder should remain visible after selection.
  value = '', // Currently selected value.
  dropdownType = 'list', // 'boolean' | 'list' - Determines the dropdown behavior
  title = '', // Title text displayed above the dropdown.
  list = [], // List of selectable options.
  onChange, // Callback function called when the selection changes.
  className, // Custom class names to apply for styling purposes.
  inputClass = '', // Custom class names to apply for styling purposes.
  arrow = 'outlined', // Determines the arrow style in the dropdown.
  showArrow = true, // Whether to show the dropdown arrow icon.
  disabled = false, // Disables interaction if true (legacy, use state='disabled')
  onlyIcon = false, // If true, only the icon of the selected item is shown.
  onlyText = false, // If true, only the text of the selected item is shown.
  multiple = false, // Allow multiple selections
  flat = false, // Flat layout for mobile
  showOptionsCount = false, // Show count of selected options
  custom = null,
  singleOptionStyle = 'radio',
  bulkActions = false, // Enable "Select all" + per-row "Only" shortcuts (multi-select only)
  onOpenChange = () => {}, // Callback function called when dropdown open state changes.
  align = 'auto', // 'auto' | 'left' | 'right' - which edge the panel is anchored to; 'auto' flips only to avoid overflowing the window.
  ariaLabel = null, // Accessible name for the trigger. Required when there's no visible `label`/`placeholder` text (e.g. icon-only dropdowns).
}) => {
  const labelId = useId()
  const [internalIsOpen, setInternalIsOpen] = useState(false) // Internal state for open/close
  const isOpen = controlledIsOpen !== null ? controlledIsOpen : internalIsOpen
  const setOpen = controlledIsOpen !== null ? onOpenChange : setInternalIsOpen

  const dropdownButtonRef = useRef(null)
  const dropdownRef = useRef(null)
  const [dropdownPosition, setDropdownPosition] = useState('left') // State to track the dropdown position
  const [hasMoreBelow, setHasMoreBelow] = useState(false) // Panel content continues below the visible area

  // Determine actual disabled and loading states
  const isDisabled = disabled || state === 'disabled'
  const isLoading = state === 'loading'
  const isActive = state === 'active' || isOpen
  const isHover = state === 'hover'

  useEffect(() => {
    // Handles closing the dropdown when clicking outside of it
    const handleClickOutside = event => {
      if (dropdownButtonRef.current && !dropdownButtonRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Cleanup the event listener on component unmount
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setOpen])

  useEffect(() => {
    if (!isOpen) return

    if (align !== 'auto') {
      setDropdownPosition(align)
      return
    }

    const dropDown = dropdownRef.current?.getBoundingClientRect()
    const dropDownButton = dropdownButtonRef.current?.getBoundingClientRect()
    const windowWidth = window.innerWidth
    if (dropDown && dropDownButton && dropDownButton.left + dropDown.width > windowWidth) {
      setDropdownPosition('right')
    } else {
      setDropdownPosition('left')
    }
  }, [isOpen, align])

  // Tracks whether generated options sit below the fold; `custom` panels manage their own content.
  const hasCustomContent = Boolean(custom)
  useEffect(() => {
    if (!isOpen || hasCustomContent) {
      setHasMoreBelow(false)
      return
    }

    let panel = null
    let resizeObserver = null
    const update = () => {
      if (!panel) return
      setHasMoreBelow(panel.scrollHeight - panel.scrollTop - panel.clientHeight > 2)
    }

    // The panel mounts through AnimatePresence, so the ref is only set after this render commits.
    const frame = requestAnimationFrame(() => {
      panel = dropdownRef.current
      if (!panel) return
      update()
      panel.addEventListener('scroll', update, { passive: true })
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(update)
        resizeObserver.observe(panel)
        // The panel's own box is pinned by max-h once it overflows; the content box is not.
        if (panel.firstElementChild) resizeObserver.observe(panel.firstElementChild)
      }
    })

    return () => {
      cancelAnimationFrame(frame)
      panel?.removeEventListener('scroll', update)
      resizeObserver?.disconnect()
    }
  }, [isOpen, list?.length, hasCustomContent])

  // Toggles the dropdown open state.
  const toggleDropdown = () => {
    if (isDisabled || isLoading) return

    if (dropdownType === 'boolean') {
      onChange(!value)
    } else {
      const newOpenState = !isOpen
      setOpen(newOpenState)
      onOpenChange(newOpenState)
    }
  }

  const handleOptionSelect = selectedValue => {
    if (multiple) {
      // Toggles selection for multiple select
      if (Array.isArray(value)) {
        const newValue = value.includes(selectedValue)
          ? value.filter(v => v !== selectedValue)
          : [...value, selectedValue]

        onChange(newValue, selectedValue)
      } else {
        onChange([selectedValue], selectedValue)
      }
    } else {
      onChange(selectedValue)
      if (dropdownType !== 'boolean') {
        setOpen(false)
        onOpenChange(false)
      }
    }
  }

  // Renders the content of the selected option based on value.
  const renderOptionContent = optionValue => {
    var item
    if (Array.isArray(optionValue)) item = optionValue.map(opi => list.find(node => node.value === opi))
    else item = list.find(node => node.value === optionValue)

    return item ? (
      <>
        {keepPlaceholder && placeholder && <span className="dropdown__placeholder">{placeholder}:</span>}
        {!onlyText && item.icon}
        {!onlyIcon && item && (Array.isArray(item) ? placeholder : item?.text)}
        {showOptionsCount && multiple && value && value.length && ` (${value.length})`}
      </>
    ) : null
  }

  // "Select all" — re-selects every option. Disabled when already all-selected.
  const renderSelectAll = () => {
    if (!bulkActions || !multiple || !Array.isArray(list) || list.length === 0) return null
    const allValues = list.map(o => o.value)
    const allSelected = Array.isArray(value) && allValues.every(v => value.includes(v))
    return (
      <Button
        type="text-only"
        size="small"
        buttonType="button"
        disabled={allSelected}
        onClick={e => {
          e?.stopPropagation?.()
          if (allSelected) return
          onChange(allValues, allValues[allValues.length - 1])
        }}>
        {i18n('filters.select_all')}
      </Button>
    )
  }

  const renderOptionList = () => {
    const options = list.map((option, idx) => {
      const isActive = multiple ? Array.isArray(value) && value.includes(option.value) : value === option.value

      // multiple → checkbox, single → radio (default) or tick (trailing check icon)
      const trailingControl = multiple ? 'checkbox' : singleOptionStyle === 'radio' ? 'radio' : 'none'

      const trailingIcon =
        !multiple && singleOptionStyle === 'tick' && isActive ? (
          <CheckIcon width="12px" fill="var(--color-brand-04)" />
        ) : undefined

      // Desktop multi-select: reveal an "Only" shortcut on row hover that keeps just this option.
      const showOnly = multiple && !flat && bulkActions

      return (
        <div key={`dropdown-option-${idx}`} className="relative group">
          <MenuItem
            text={option.text}
            icon={option.icon}
            trailingControl={trailingControl}
            trailingIcon={trailingIcon}
            active={isActive}
            closeMenuOnClick={false}
            onClick={e => {
              e?.stopPropagation?.()
              handleOptionSelect(option.value)
            }}
          />
          {showOnly && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 flex items-center px-2 py-1 rounded text-xs font-bold text-[var(--color-brand-04)] bg-surface-1 hover:bg-Neutral-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity"
              onClick={e => {
                e.stopPropagation()
                onChange([option.value], option.value)
              }}>
              {i18n('filters.only')}
            </button>
          )}
        </div>
      )
    })

    return (
      <>
        {options}
        {/* Desktop: "Select all" at the bottom. (Mobile shows it next to the title.) */}
        {multiple && !flat && <div className="px-2 py-1 flex justify-center">{renderSelectAll()}</div>}
      </>
    )
  }

  const renderOptionBinary = () => {
    return (
      <div className="dropdown__option" onClick={toggleDropdown}>
        <div className="flex items-center gap-2 w-full">
          <RadioControl active={value} />
          {placeholder}
        </div>
      </div>
    )
  }

  const listStyle = dropdownPosition === 'right' ? '!left-auto !right-0' : '!left-0 !right-auto'

  // Dynamically generates class names for the dropdown based on its state and props.
  const dropdownClass = classNames('dropdown', className, {
    // Type classes
    [`dropdown--type-${type}`]: type,

    // State classes
    [`dropdown--state-${state}`]: state,

    // Size classes
    [`dropdown--size-${size}`]: size,

    // Feature classes
    'dropdown--selected': value && value !== 'all' && dropdownType === 'boolean',
    'dropdown--list': dropdownType === 'list',
    'dropdown--open': isActive && !isDisabled && !isLoading,
    'dropdown--multiple': multiple,
    'dropdown--flat': flat,
    'dropdown--has-label': hasLabel || !!label,
  })

  const valueClass = classNames('dropdown__value', inputClass)

  // Loading state handled by overlay, no special render needed

  if (flat) {
    return (
      <div className="w-full bg-surface-2">
        {(title || renderSelectAll()) && (
          <div className="flex w-full items-center gap-2 my-3 px-3">
            {title && <div className="font-bold capitalize">{title}</div>}
            <div className="ml-auto shrink-0">{renderSelectAll()}</div>
          </div>
        )}
        {dropdownType === 'boolean' && (custom || renderOptionBinary())}
        {dropdownType === 'list' && <div className="w-full">{custom || renderOptionList()}</div>}
      </div>
    )
  }

  const dropdownElement = (
    <div
      className={dropdownClass}
      onClick={toggleDropdown}
      ref={dropdownButtonRef}
      onKeyDown={e => {
        if ((e.key === 'Enter' || e.key === ' ') && !isDisabled && !isLoading) {
          e.preventDefault()
          toggleDropdown()
        }
      }}>
      <div
        className={valueClass + ' outline-offset-2 focus-visible:outline-[var(--color-brand-04)]'}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={isDisabled || undefined}
        aria-label={ariaLabel || undefined}
        aria-labelledby={!ariaLabel && (hasLabel || label) ? labelId : undefined}>
        {iconStart && <i className="dropdown__icon-start">{iconStart}</i>}

        {dropdownType === 'list' && value !== undefined && value !== null && value !== ''
          ? renderOptionContent(value)
          : placeholder && <span className="dropdown__placeholder">{placeholder}</span>}

        {dropdownType === 'list' &&
          !isLoading &&
          showArrow &&
          (arrow === 'filled' ? (
            <i className="dropdown__arrow" />
          ) : (
            <i className="dropdown__icon">
              <Crevron width="11px" height="7px" fillcolor="currentColor" />
            </i>
          ))}
      </div>

      {/* Shimmer loading overlay */}
      {isLoading && <div className="dropdown__shimmer-overlay animate-pulse" />}

      {(list || custom) && (
        <FloatingPanel
          ref={dropdownRef}
          isOpen={isOpen && !isDisabled && !isLoading}
          initial={{ opacity: 0, scaleY: 0.8 }}
          animate={{ opacity: 1, scaleY: 1 }}
          exit={{ opacity: 0, scaleY: 0.8 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30, mass: 0.7 }}
          // Option lists show 5 rows (5 × 48px) plus the 20px hint strip; custom panels keep 50vh.
          className={`dropdown__list left-[-4px] top-[calc(100%+8px)] min-w-[270px] ${
            hasCustomContent ? 'max-h-[50vh]' : 'max-h-[260px]'
          } m-0 p-0 origin-top ${listStyle}`}>
          {custom || renderOptionList()}
          {/* Sticky footer strip with a down chevron while more options sit below the fold */}
          {hasMoreBelow && (
            <div
              aria-hidden="true"
              className="dropdown__scroll-hint sticky bottom-0 flex h-5 w-full items-center justify-center pointer-events-none text-Neutral-4">
              <Crevron width="11px" height="7px" fillcolor="currentColor" />
            </div>
          )}
        </FloatingPanel>
      )}
    </div>
  )

  if (!label) return dropdownElement

  return (
    <div className="dropdown-field">
      <span id={labelId} className="dropdown-field__label">{label}</span>
      {dropdownElement}
    </div>
  )
}

// PropTypes for type-checking the props passed to the component.
Dropdown.propTypes = {
  // Design System Props
  type: PropTypes.oneOf(['default', 'outline']),
  state: PropTypes.oneOf(['default', 'hover', 'active', 'disabled', 'loading']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fontSize: PropTypes.oneOf(['12px', '14px']),
  iconStart: PropTypes.element,
  hasLabel: PropTypes.bool,
  label: PropTypes.node,
  isOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  align: PropTypes.oneOf(['auto', 'left', 'right']),

  // Existing Props
  placeholder: PropTypes.string,
  keepPlaceholder: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  dropdownType: PropTypes.oneOf(['boolean', 'list']),
  list: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      text: PropTypes.string,
      icon: PropTypes.element,
      tag: PropTypes.string,
    })
  ),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  arrow: PropTypes.string,
  disabled: PropTypes.bool,
  onlyIcon: PropTypes.bool,
  onlyText: PropTypes.bool,
  multiple: PropTypes.bool,
  flat: PropTypes.bool,
  showOptionsCount: PropTypes.bool,
  singleOptionStyle: PropTypes.oneOf(['radio', 'tick']),
}

export default Dropdown
